import React, { useState, useRef } from "react";
import { useLocation } from "react-router-dom";

export default function ContactFormSection() {
  const location = useLocation();
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Do not render this section on the dedicated contact page
  if (location.pathname === "/kontakt") {
    return null;
  }

  // Client-side file validation constraints
  const MAX_FILES = 5;
  const MAX_FILE_SIZE_MB = 10;
  const MAX_TOTAL_SIZE_MB = 20;
  const ALLOWED_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/gif", "image/webp"];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.value ? e.target.files : []);
    if (files.length === 0) return;

    // Validate file count
    if (files.length > MAX_FILES) {
      alert(`Du kan højst uploade ${MAX_FILES} filer.`);
      e.target.value = "";
      setSelectedFiles([]);
      return;
    }

    // Validate individual file size and type
    let totalSize = 0;
    for (let file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`Filtypen for "${file.name}" er ikke tilladt. Kun PDF, PNG, JPEG, GIF og WEBP er tilladt.`);
        e.target.value = "";
        setSelectedFiles([]);
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        alert(`Filen "${file.name}" er for stor. Hver fil må højst fylde ${MAX_FILE_SIZE_MB} MB.`);
        e.target.value = "";
        setSelectedFiles([]);
        return;
      }
      totalSize += file.size;
    }

    // Validate total size
    if (totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
      alert(`Den samlede størrelse af filerne overstiger grænsen på ${MAX_TOTAL_SIZE_MB} MB.`);
      e.target.value = "";
      setSelectedFiles([]);
      return;
    }

    setSelectedFiles(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage("");

    const form = formRef.current;
    const formData = new FormData(form);

    // Verify honeypot field is empty
    const honeypot = formData.get("website_url");
    if (honeypot) {
      // Silent fail for bots
      setIsSubmitting(false);
      setSubmitStatus("success");
      form.reset();
      setSelectedFiles([]);
      return;
    }

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        body: formData,
        // Content-Type is set automatically by the browser with multipart boundary
      });

      const result = await response.json();

      if (response.ok && result.status === "ok") {
        setSubmitStatus("success");
        form.reset();
        setSelectedFiles([]);
      } else {
        setSubmitStatus("error");
        setErrorMessage(result.message || "Der opstod en fejl under afsendelsen. Prøv venligst igen.");
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setSubmitStatus("error");
      setErrorMessage("Netværksfejl. Kontroller din internetforbindelse og prøv igen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative py-20 bg-brand-sand-100 border-t border-brand-sand-200/60 overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Text & Info */}
          <div className="lg:col-span-5 flex flex-col gap-6 text-left">
            <div className="flex flex-col gap-3">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-green-900 leading-tight">
                Skal vi hjælpe dig med dit næste haveprojekt?
              </h2>
            </div>
            
            <p className="text-brand-charcoal/80 text-sm md:text-base leading-relaxed">
              Udfyld formularen med dine ønsker, så vender vi tilbage til dig med et uforpligtende tilbud hurtigst muligt. Du er også altid velkommen til at ringe eller sende os en e-mail direkte.
            </p>

            {/* Direct Contact shortcuts */}
            <div className="pt-4 flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-phone text-[#025e40] text-lg shrink-0 w-5 text-center" />
                <a href="tel:+4520661517" className="text-brand-green-900 font-bold hover:text-[#7ca632] transition-colors">
                  +45 20 66 15 17
                </a>
              </div>

              <div className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-[#025e40] text-lg shrink-0 w-5 text-center" />
                <a href="mailto:rasmus@elmegaardenhaveoganlaeg.dk" className="text-brand-green-900 font-bold hover:text-[#7ca632] transition-colors">
                  rasmus@elmegaardenhaveoganlaeg.dk
                </a>
              </div>

              <div className="flex items-center gap-3">
                <i className="fa-solid fa-location-dot text-[#025e40] text-lg shrink-0 w-5 text-center" />
                <span className="text-brand-green-900 font-bold">
                  Stenstrupvej 5, 9500 Hobro
                </span>
              </div>
            </div>
          </div>

          {/* Right Column: Webform Card */}
          <div className="lg:col-span-7 bg-white -mx-6 px-6 py-8 sm:mx-0 sm:p-8 md:p-10 sm:rounded-sm border-y sm:border border-brand-sand-200/80 shadow-lg text-left">
            <h3 className="font-sans text-xl md:text-2xl font-bold tracking-tight text-[#025e40] mb-6">
              Send os en besked i dag
            </h3>

            {submitStatus === "success" && (
              <div className="mb-6 p-5 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-sm flex items-start gap-4">
                <i className="fa-solid fa-circle-check text-emerald-600 text-xl shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-base">Tak for din henvendelse!</h4>
                  <p className="text-sm text-emerald-800 mt-1">
                    Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.
                  </p>
                </div>
              </div>
            )}

            {submitStatus === "error" && (
              <div className="mb-6 p-5 bg-rose-50 border border-rose-200 text-rose-900 rounded-sm flex items-start gap-4">
                <i className="fa-solid fa-circle-exclamation text-rose-600 text-xl shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-base">Der opstod en fejl</h4>
                  <p className="text-sm text-rose-800 mt-1">{errorMessage}</p>
                </div>
              </div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-5">
              {/* Antispam Honeypot - ABSOLUTELY HIDDEN */}
              <div className="sr-only absolute" aria-hidden="true">
                <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke denne)</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              {/* Name & Email Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="form_name" className="text-xs font-bold uppercase tracking-wider text-brand-charcoal/70">
                    Navn <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="form_name"
                    name="name"
                    required
                    placeholder="Dit fulde navn"
                    className="w-full px-4 py-3 bg-brand-sand-50 border border-brand-sand-200 rounded-sm focus:outline-none focus:border-[#025e40] focus:ring-1 focus:ring-[#025e40] transition-all text-sm"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="form_email" className="text-xs font-bold uppercase tracking-wider text-brand-charcoal/70">
                    E-mail <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="form_email"
                    name="email"
                    required
                    placeholder="din@email.dk"
                    className="w-full px-4 py-3 bg-brand-sand-50 border border-brand-sand-200 rounded-sm focus:outline-none focus:border-[#025e40] focus:ring-1 focus:ring-[#025e40] transition-all text-sm"
                  />
                </div>
              </div>

              {/* Phone & Subject Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="form_phone" className="text-xs font-bold uppercase tracking-wider text-brand-charcoal/70">
                    Telefon <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="form_phone"
                    name="phone"
                    required
                    placeholder="Tlf. nummer"
                    className="w-full px-4 py-3 bg-brand-sand-50 border border-brand-sand-200 rounded-sm focus:outline-none focus:border-[#025e40] focus:ring-1 focus:ring-[#025e40] transition-all text-sm"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="form_subject" className="text-xs font-bold uppercase tracking-wider text-brand-charcoal/70">
                    Emne / Ydelse
                  </label>
                  <select
                    id="form_subject"
                    name="subject"
                    className="w-full px-4 py-3 bg-brand-sand-50 border border-brand-sand-200 rounded-sm focus:outline-none focus:border-[#025e40] focus:ring-1 focus:ring-[#025e40] transition-all text-sm appearance-none"
                    defaultValue="Uforpligtende tilbud"
                  >
                    <option value="Uforpligtende tilbud">Uforpligtende tilbud</option>
                    <option value="Belægning & brolægning">Belægning & brolægning</option>
                    <option value="Grøn vedligeholdelse">Grøn vedligeholdelse</option>
                    <option value="Anlæg af have">Anlæg af have</option>
                    <option value="Havedesign & rådgivning">Havedesign & rådgivning</option>
                    <option value="Andet">Andet</option>
                  </select>
                </div>
              </div>

              {/* Message */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="form_message" className="text-xs font-bold uppercase tracking-wider text-brand-charcoal/70">
                  Besked <span className="text-rose-500">*</span>
                </label>
                <textarea
                  id="form_message"
                  name="message"
                  required
                  rows={4}
                  placeholder="Beskriv dit projekt, dine ønsker og eventuelle mål..."
                  className="w-full px-4 py-3 bg-brand-sand-50 border border-brand-sand-200 rounded-sm focus:outline-none focus:border-[#025e40] focus:ring-1 focus:ring-[#025e40] transition-all text-sm resize-none"
                />
              </div>

              {/* File Upload */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="form_files" className="text-xs font-bold uppercase tracking-wider text-brand-charcoal/70 flex justify-between items-center">
                  <span>Vedhæft billeder eller filer (valgfrit)</span>
                  <span className="text-[10px] text-brand-charcoal/50 normal-case font-normal">Max 5 filer, i alt 20 MB</span>
                </label>
                <div className="relative border border-dashed border-brand-sand-300 hover:border-[#025e40] rounded-sm bg-brand-sand-50/50 p-4 transition-colors text-center cursor-pointer">
                  <input
                    type="file"
                    id="form_files"
                    name="attachments"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="flex flex-col items-center gap-1 pointer-events-none">
                    <i className="fa-light fa-cloud-arrow-up text-brand-charcoal/40 text-xl" />
                    <span className="text-xs text-brand-charcoal/70 font-medium">
                      {selectedFiles.length > 0
                        ? `${selectedFiles.length} fil(er) valgt`
                        : "Klik eller træk filer hertil for at uploade"}
                    </span>
                    <span className="text-[10px] text-brand-charcoal/40">
                      PDF, PNG, JPG, JPEG, GIF, WEBP (Max 10 MB pr. fil)
                    </span>
                  </div>
                </div>

                {/* Selected Files List */}
                {selectedFiles.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 px-2.5 py-1 bg-brand-sand-100 border border-brand-sand-200 rounded-full text-xs text-brand-green-950">
                        <i className="fa-light fa-file text-[10px]" />
                        <span className="max-w-[150px] truncate">{file.name}</span>
                        <span className="text-[10px] text-brand-charcoal/50">({(file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-2 py-4 px-6 bg-[#7ca632] hover:bg-[#6b9128] text-white font-bold text-sm tracking-wide rounded-sm transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" />
                    <span>Sender...</span>
                  </>
                ) : (
                  <>
                    <span>Send forespørgsel</span>
                    <i className="fa-solid fa-paper-plane text-xs" />
                  </>
                )}
              </button>
            </form>
          </div>

        </div>
      </div>
    </section>
  );
}
